import React, { useMemo, useRef, useLayoutEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { makeAsphalt } from "../lib/textures";

// Yellow taxis that climb the ramps and cross the bridge decks (borough to borough).
const DECK_LOOPS = [
  [[188, 1, 150], [382, 42, 150], [1250, 43, 150], [1250, 43, 158], [382, 42, 158], [188, 1, 158]],
  [[188, 1, -248], [382, 47, -248], [1250, 48, -248], [1250, 48, -240], [382, 47, -240], [188, 1, -240]],
];
function BridgeTaxis() {
  const ref = useRef();
  const curves = useMemo(
    () => DECK_LOOPS.map((pts) => new THREE.CatmullRomCurve3(pts.map(([x, y, z]) => new THREE.Vector3(x, y, z)), true, "catmullrom", 0.1)),
    []
  );
  const cars = useMemo(() => {
    const arr = [];
    curves.forEach((curve) => {
      for (let i = 0; i < 6; i++) arr.push({ curve, u: i / 6, speed: 0.02 + Math.random() * 0.01 });
    });
    return arr;
  }, [curves]);
  const d = useMemo(() => new THREE.Object3D(), []);
  const p = useMemo(() => new THREE.Vector3(), []);
  const t = useMemo(() => new THREE.Vector3(), []);
  const set = (i, c) => {
    c.curve.getPointAt(c.u, p);
    c.curve.getTangentAt(c.u, t);
    d.position.set(p.x, p.y + 1.4, p.z);
    d.rotation.set(0, Math.atan2(t.x, t.z), 0);
    d.updateMatrix();
    ref.current.setMatrixAt(i, d.matrix);
  };
  useLayoutEffect(() => {
    cars.forEach((c, i) => set(i, c));
    ref.current.instanceMatrix.needsUpdate = true;
  });
  useFrame((_, delta) => {
    if (!ref.current) return;
    cars.forEach((c, i) => {
      c.u = (c.u + c.speed * delta) % 1;
      set(i, c);
    });
    ref.current.instanceMatrix.needsUpdate = true;
  });
  return (
    <instancedMesh ref={ref} args={[null, null, cars.length]} castShadow frustumCulled={false}>
      <boxGeometry args={[3, 2.2, 6]} />
      <meshStandardMaterial color="#f5c518" metalness={0.3} roughness={0.5} />
    </instancedMesh>
  );
}

// Straight ground road segment with asphalt, dashed yellow centre line + white edge lines.
function Road({ from, to, width = 22, y = 0.32, asphalt }) {
  const dx = to[0] - from[0];
  const dz = to[1] - from[1];
  const len = Math.hypot(dx, dz);
  const ang = Math.atan2(dx, dz);
  const mid = [(from[0] + to[0]) / 2, y, (from[1] + to[1]) / 2];
  const dashes = useMemo(() => {
    const arr = [];
    const n = Math.floor(len / 8);
    for (let i = 0; i < n; i++) arr.push(-len / 2 + 4 + i * 8);
    return arr;
  }, [len]);
  return (
    <group position={mid} rotation={[0, ang, 0]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[width, len]} />
        <meshStandardMaterial map={asphalt} color="#3a3d42" roughness={0.68} metalness={0.18} />
      </mesh>
      {dashes.map((z, i) => (
        <mesh key={`d${i}`} position={[0, 0.03, z]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[1.0, 4]} />
          <meshBasicMaterial color="#ffd21a" />
        </mesh>
      ))}
      {[-1, 1].map((s) => (
        <mesh key={`e${s}`} position={[s * (width / 2 - 1), 0.03, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.4, len]} />
          <meshBasicMaterial color="#d8d8d2" />
        </mesh>
      ))}
      {/* 3D concrete curbs separating the sidewalk from the asphalt (shadow gap) */}
      {[-1, 1].map((s) => (
        <mesh key={`c${s}`} position={[s * (width / 2 + 0.6), 0.45, 0]} castShadow receiveShadow>
          <boxGeometry args={[1.2, 0.8, len]} />
          <meshStandardMaterial color="#b7b3aa" roughness={0.92} metalness={0.02} />
        </mesh>
      ))}
    </group>
  );
}

// Zebra crosswalk across a road.
function Crosswalk({ pos, rot = 0, width = 22 }) {
  const span = width - 4;
  const n = 7;
  const bars = useMemo(() => Array.from({ length: n }, (_, i) => -span / 2 + (i + 0.5) * (span / n)), [span]);
  return (
    <group position={pos} rotation={[0, rot, 0]}>
      {bars.map((x, i) => (
        <mesh key={i} position={[x, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[1.2, 5]} />
          <meshBasicMaterial color="#eeeeea" />
        </mesh>
      ))}
    </group>
  );
}

// Sloped asphalt ramp connecting the street grid up to a bridge deck.
function Ramp({ from, to, width = 16, asphalt }) {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const dz = to[2] - from[2];
  const horiz = Math.hypot(dx, dz);
  const len3d = Math.hypot(horiz, dy);
  const yaw = Math.atan2(dx, dz);
  const pitch = Math.atan2(dy, horiz);
  const mid = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2, (from[2] + to[2]) / 2];
  return (
    <group position={mid} rotation={[0, yaw, 0]}>
      <group rotation={[pitch, 0, 0]}>
        <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
          <planeGeometry args={[width, len3d]} />
          <meshStandardMaterial map={asphalt} color="#3a3d42" roughness={0.68} metalness={0.18} />
        </mesh>
      </group>
    </group>
  );
}

// Manhattan street grid around the WTC superblock + ramps onto the bridge decks.
export default function Roads() {
  const asphalt = useMemo(() => makeAsphalt(), []);
  // ring-road corners (aligned to the Traffic loops)
  const SW = [-232, -252], SE = [188, -252], NE = [188, 212], NW = [-232, 212];
  return (
    <group>
      {/* perimeter ring road */}
      <Road from={SW} to={SE} asphalt={asphalt} />
      <Road from={NW} to={NE} asphalt={asphalt} />
      <Road from={SW} to={NW} asphalt={asphalt} />
      <Road from={SE} to={NE} asphalt={asphalt} />
      {/* continuous ramps climbing from the ring road straight up to the bridge decks */}
      <Ramp from={[188, 0.4, 150]} to={[382, 42, 150]} width={16} asphalt={asphalt} />
      <Ramp from={[188, 0.4, -248]} to={[382, 47, -248]} width={16} asphalt={asphalt} />
      {/* crosswalks at the plaza entrances */}
      <Crosswalk pos={[-22, 0, -252]} rot={0} />
      <Crosswalk pos={[-22, 0, 212]} rot={0} />
      <Crosswalk pos={[-232, 0, -20]} rot={Math.PI / 2} />
      <Crosswalk pos={[188, 0, -20]} rot={Math.PI / 2} />
      <BridgeTaxis />
    </group>
  );
}
